/**
 * How a sensor channel is named — the one algorithm, shared by the dashboard
 * cards (through `useSensorNames`), the alert picker and the alert condition
 * text, so the same sensor can never be called two things.
 *
 * Mirrored by the backend's `apps/stations/sensor_names.py`, which names the
 * sensor in the push notification. The two must agree.
 */

export type SensorNames = Record<string, string> | null | undefined;

/** The "Ch3"-style fallback for a channel the user has not named. */
export const getDefaultChannelName = (metric: string): string | undefined => {
  const match = metric.match(/CH(\d+)/);
  if (!match) {
    return undefined;
  }
  const chNum = match[1];
  // Distinguish temperature sensor types in the merged card
  if (metric.startsWith('TEMP_AND_HUMIDITY_')) {
    return `Ch${chNum} T+H`;
  }
  if (metric.startsWith('TEMP_CH')) {
    return `Ch${chNum} T`;
  }
  return `Ch${chNum}`;
};

/** The user's own name for the sensor `metric` belongs to, if any. */
export const resolveGivenSensorName = (
  metric: string,
  sensorNames: SensorNames,
): string | undefined => {
  const names = sensorNames || {};

  // Exact match (e.g. dashboard passes prefix keys like 'TEMP_AND_HUMIDITY_CH1_')
  if (names[metric]) {
    return names[metric];
  }

  // Legacy: temperature sensors stored names under exact key (e.g. 'TEMP_CH1_T')
  // but are now addressed by prefix ('TEMP_CH1_')
  if (metric.endsWith('_') && names[`${metric}T`]) {
    return names[`${metric}T`];
  }

  // Prefix match (e.g. charts pass full keys like 'TEMP_AND_HUMIDITY_CH1_T'
  // but sensor names are stored under the prefix key)
  const prefixKey = Object.keys(names).find(
    (key) => key.endsWith('_') && metric.startsWith(key),
  );
  return prefixKey ? names[prefixKey] : undefined;
};
